import { REPOSITORY_CONNECTOR_TYPES } from '@/components/projects/connectors/connectorConstants'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { getErrorMessage } from '@/api/errors'
import { useConnectors, useProviderRepositories, useUploadContextRepositories } from '@/hooks'
import { extractValidationMessage } from '@/lib/validationErrors'
import { cn } from '@/lib/utils'
import type { ConnectorType, ProviderRepositoryInfo } from '@/types'
import { BookOpen, ChevronDown, Code, Plus, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

type RepositoryUploadItem = {
  id: string
  repo: ProviderRepositoryInfo
  description: string
  connectorId: string
  connectorType: ConnectorType
  connectionName: string
}

function connectionLabel(connector: { connectionName?: string; type: string }): string {
  return connector.connectionName || connector.type
}

function getPickerPlaceholder(selectedCount: number, hasConnector: boolean): string {
  if (selectedCount > 0) return 'Add more...'
  return hasConnector ? 'Select repositories' : 'Select connector first'
}

interface RepositoriesUploadDialogProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly projectId: string
}

export function RepositoriesUploadDialog({ open, onOpenChange, projectId }: RepositoriesUploadDialogProps) {
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>('')
  const [repoSearch, setRepoSearch] = useState('')
  const [items, setItems] = useState<RepositoryUploadItem[]>([])
  const uploadRepoMutation = useUploadContextRepositories(projectId);

  const { data: connectors = [] } = useConnectors(projectId)

  const connectorOptions = useMemo(
    () => connectors.filter((c) => REPOSITORY_CONNECTOR_TYPES.includes(c.type)),
    [connectors]
  )

  const connectorId =
    selectedConnectorId && connectorOptions.some((c) => c.id === selectedConnectorId)
      ? selectedConnectorId
      : (connectorOptions.length === 1 ? connectorOptions[0].id : '')
  const connector = connectorOptions.find((c) => c.id === connectorId)
  const connectorType = (connector?.type ?? '') as ConnectorType | ''

  const { data: repositories = [] } = useProviderRepositories(
    projectId,
    connectorType as ConnectorType,
    Boolean(connectorId),
    '',
    connectorId || undefined
  )

  const addedRepoIds = useMemo(
    () => new Set(items.filter((item) => item.connectorId === connectorId).map((item) => item.repo.id)),
    [items, connectorId]
  )

  const availableRepos = useMemo(
    () =>
      repositories
        .filter((r) => !addedRepoIds.has(r.id))
        .filter((r) => r.fullName.toLowerCase().includes(repoSearch.toLowerCase())),
    [repositories, addedRepoIds, repoSearch]
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedConnectorId('')
      setRepoSearch('')
      setItems([])
    }
    onOpenChange(nextOpen)
  }

  const handleSelectRepo = (repo: ProviderRepositoryInfo) => {
    if (!connector) return
    setItems((prev) => [
      ...prev,
      {
        id: `${connector.id}-${repo.id}-${crypto.getRandomValues(new Uint32Array(1))[0]}`,
        repo,
        description: '',
        connectorId: connector.id,
        connectorType: connector.type,
        connectionName: connectionLabel(connector),
      },
    ])
  }

  const canUpload = items.length > 0;

  const handleUpload = async () => {
    if (!canUpload) return

    try {
      const response = await uploadRepoMutation.mutateAsync({
        repositories: items.map((item) => ({
          connectorType: item.connectorType,
          connectorId: item.connectorId,
          repositoryId: item.repo.id,
          repositoryFullName: item.repo.fullName,
          repositoryUrl: item.repo.url,
          description: item.description,
        })),
      })
      toast.success('Uploaded successfully')
      if (response.skippedCount > 0) {
        toast.info(
          `${response.skippedCount} ${response.skippedCount === 1 ? 'repository was' : 'repositories were'} already in context and ${response.skippedCount === 1 ? 'was' : 'were'} skipped.`
        )
      }
      handleOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Upload failed')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>Upload Repositories</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="w-1/2">
            <label className="text-sm font-medium">Connector *</label>
            <Select
              value={connectorId}
              disabled={connectorOptions.length === 1}
              onValueChange={(id) => {
                setSelectedConnectorId(id)
                setRepoSearch('')
              }}
            >
              <SelectTrigger className="mt-2" aria-label="Select connector">
                <SelectValue placeholder="Select connector" />
              </SelectTrigger>
              <SelectContent>
                {connectorOptions.map((connector) => (
                  <SelectItem key={connector.id} value={connector.id}>
                    {connectionLabel(connector)}
                    {connector.connectionName && connector.connectionName !== connector.type && (
                      <span className="ml-2 text-xs text-muted-foreground">{connector.type}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Repository *</label>
            <div className="mt-2 flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={!connectorType}>
                  <div
                    role="combobox"
                    aria-label="Select repositories"
                    tabIndex={!connectorType ? -1 : 0}
                    className={cn(
                      'flex min-h-10 flex-1 flex-wrap items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 cursor-pointer',
                      !connectorType && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    {items.length === 0 && (
                      <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    {items.map((item) => (
                      <span key={item.id} className="flex items-center gap-1 rounded-full border bg-secondary px-2.5 py-0.5 text-sm">
                        {item.repo.fullName}
                        <span className="text-xs text-muted-foreground">({item.connectionName})</span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label={`Remove ${item.repo.fullName}`}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
                          }}
                          onClick={() => setItems((prev) => prev.filter((entry) => entry.id !== item.id))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      {getPickerPlaceholder(items.length, Boolean(connectorType))}
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
                  <div className="relative border-b p-2">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={repoSearch}
                      onChange={(e) => setRepoSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Search and select repositories"
                      className="pl-8"
                      aria-label="Search and select repositories"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {availableRepos.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No repositories found.</div>
                    ) : (
                      availableRepos.map((repo) => (
                        <DropdownMenuItem
                          key={repo.id}
                          onSelect={(e) => e.preventDefault()}
                          onClick={() => handleSelectRepo(repo)}
                          className="flex items-center gap-2"
                        >
                          {repo.fullName}
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} data-testid="repository-row" className="grid grid-cols-[1fr_1fr_36px] items-center gap-3 rounded-lg border p-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="rounded border p-2 text-muted-foreground shrink-0">
                  <Code className="h-4 w-4" />
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm font-medium truncate">{item.repo.fullName}</span>
                    </TooltipTrigger>
                    <TooltipContent>{item.repo.fullName}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="shrink-0 text-xs text-muted-foreground" data-testid="repository-connection">{item.connectionName}</span>
              </div>
              <Input
                placeholder="Description (Optional)"
                value={item.description}
                data-testid="repository-description"
                aria-label={`Description for ${item.repo.fullName}`}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((entry) =>
                      entry.id === item.id ? { ...entry, description: e.target.value } : entry
                    )
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${item.repo.fullName}`}
                onClick={() => setItems((prev) => prev.filter((entry) => entry.id !== item.id))}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload || uploadRepoMutation.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
